import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Camera, Mic, Lightbulb, Film } from "lucide-react";

const features = [
  { icon: Camera, label: "Multicámara 4K", detail: "3-4 cámaras Sony Alpha con planos dinámicos" },
  { icon: Mic, label: "Sonido de estudio", detail: "Micros Shure/Rode y sala tratada acústicamente" },
  { icon: Lightbulb, label: "Iluminación cinemática", detail: "Setup profesional adaptado a cada set" },
  { icon: Film, label: "Postproducción premium", detail: "Edición con ritmo, motion graphics y clips" },
];

const shots = [
  { src: "/images/studio-1.jpg", alt: "Grabación de podcast en el estudio Galvani" },
  { src: "/images/studio-2.jpg", alt: "Producción multicámara profesional" },
  { src: "/images/studio-3.jpg", alt: "Sala de control con monitorización en directo" },
  { src: "/images/studio-4.jpg", alt: "Contenido multicanal entregado listo para publicar" },
];

export default function StudioGallery() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="section-spacing bg-galvani-bg" data-testid="studio-gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-galvani-cyan font-body font-semibold mb-4">
            Nuestro estudio en Madrid
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-4">
            Calidad <span className="gradient-text">televisiva</span>, sin la burocracia de la tele
          </h2>
          <p className="font-body text-galvani-textLight max-w-2xl mx-auto">
            Un espacio pensado para que salgas guapo, sonando genial y sintiéndote cómodo.
            También grabamos en tu oficina si lo prefieres.
          </p>
        </motion.div>

        {/* Gallery grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-14">
          {shots.map((shot, i) => (
            <motion.div
              key={shot.src}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="relative group overflow-hidden rounded-2xl aspect-[4/3] shadow-lg"
            >
              <picture>
                <source srcSet={shot.src.replace(".jpg", ".webp")} type="image/webp" />
                <img
                  src={shot.src}
                  alt={shot.alt}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  width="854"
                  height="480"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </motion.div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {features.map((f, i) => (
            <motion.div
              key={f.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
              className="p-5 rounded-2xl bg-white border border-slate-200 text-center"
            >
              <div className="w-11 h-11 mx-auto rounded-xl bg-gradient-to-br from-galvani-blue to-galvani-purple flex items-center justify-center mb-3">
                <f.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="font-heading font-semibold text-galvani-text text-sm mb-1">{f.label}</h3>
              <p className="font-body text-xs text-galvani-textLight leading-relaxed">{f.detail}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
